import React__default from 'react';
import styled, { keyframes } from 'styled-components';
import Dropdown from '../../components/Dropdown/Dropdown.js';
import Box from '../../components/Box/Box.js';
import Text from '../../components/Text/Text.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

// --------------------------------------------------------
// Rule Builder Header Menu
// @TODO: combine with GlobalHeaderMenu and abstract into an Atlassian specific Dropdown
/**
 * Displays a dropdown menu that can be triggered with a button and that allows a user to make a single selection.
 */
const RuleBuilderHeaderMenu = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledDropdown, null, children);
};
RuleBuilderHeaderMenu.displayName = "RuleBuilderHeaderMenu";
const StyledDropdown = styled(Dropdown).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledDropdown",
  componentId: "sc-1uybqoa-0"
})([""]);
const slideUpAndFade = keyframes(["from{opacity:0;transform:translateY(2px);}to{opacity:1;transform:translateY(0);}"]);
const slideRightAndFade = keyframes(["from{opacity:0;transform:translateX(-2px);}to{opacity:1;transform:translateX(0);}"]);
const slideDownAndFade = keyframes(["from{opacity:0;transform:translateY(-2px);}to{opacity:1;transform:translateY(0);}"]);
const slideLeftAndFade = keyframes(["from{opacity:0;transform:translateX(2px);}to{opacity:1;transform:translateX(0);}"]);
/**
 * Displays a dropdown menu item. Should be rendered inside a RuleBuilderHeader Menu.
 */
const Item = ({
  children,
  disabled,
  description,
  onSelect,
  href,
  sx,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledMenuItem, _extends({
    disabled: disabled,
    onSelect: onSelect,
    href: href,
    sx: sx
  }, props), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  }, children, /*#__PURE__*/React__default.createElement(React__default.Fragment, null, description ? /*#__PURE__*/React__default.createElement(Box, {
    as: "span"
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    color: disabled ? 'text.disabled' : 'text.subtlest',
    sx: {
      fontSize: 1,
      marginTop: 1,
      wordBreak: 'break-word'
    }
  }, description)) : null)));
};
Item.displayName = "Item";
const StyledMenuItem = styled(Dropdown.Item).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledMenuItem",
  componentId: "sc-1uybqoa-1"
})(["font-size:", ";line-height:1;min-height:40px;color:", ";display:flex;align-items:center;height:", ";padding:0 ", ";position:relative;padding-left:", ";user-select:none;outline:none;text-align:left;margin:0;text-decoration:none;&[data-highlighted]{color:", ";background-color:", ";cursor:pointer;}&[data-disabled]{color:", ";}&:hover{text-decoration:none;background-color:", ";}&:active{background-color:", ";color:", ";}&:visited{color:", ";}", ""], get('fontSizes.3'), get('colors.text.default'), get('space.5'), get('space.2'), get('space.3'), get('colors.text.default'), get('colors.background.neutral.subtle.hovered'), get('colors.text.disabled'), get('colors.background.neutral.subtle.hovered'), get('colors.background.selected.default.[default]'), get('colors.text.selected'), get('colors.text.selected'), sx);
/**
 * The dropdown menu trigger. Must be rendered inside a RuleBuilderHeader Menu.
 */
const Trigger = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledMenuTrigger, null, children);
};
Trigger.displayName = "Trigger";
const StyledMenuTrigger = styled(Dropdown.Trigger).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledMenuTrigger",
  componentId: "sc-1uybqoa-2"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;cursor:pointer;text-align:left;font:inherit;user-select:none;display:flex;align-items:center;color:inherit;font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:", " 12px;border-radius:", ";&:hover,&:focus{opacity:1;text-decoration:none;color:", ";background-color:", ";}&:active{opacity:1;text-decoration:none;background-color:", ";color:", ";}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}svg{transition:transform 0.15s ease;}&[data-state='open']{background-color:", ";color:", ";}", ""], get('fontSizes.3'), get('fontWeights.medium'), get('space.2'), get('radii.1'), get('colors.link.default'), get('colors.background.neutral.subtle.hovered'), get('colors.background.selected.default.[default]'), get('colors.text.selected'), get('colors.border.focused'), get('colors.border.focused'), get('colors.background.selected.default.[default]'), get('colors.text.selected'), sx);
/**
 * The dropdown menu content that pops out when the dropdown menu is open.
 */
const Content = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledMenuContent, props, children);
};
Content.displayName = "Content";
const StyledMenuContent = styled(Dropdown.Content).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledMenuContent",
  componentId: "sc-1uybqoa-3"
})(["min-width:220px;max-width:800px;max-height:calc(100vh - 16px);position:absolute;right:-24px;overflow:auto;display:flex;flex-direction:column;background-color:", ";border:1px solid ", ";border-radius:", ";padding:", " 0;box-shadow:", ";animation-duration:400ms;animation-timing-function:cubic-bezier(0.16,1,0.3,1);will-change:transform,opacity;&[data-side='top']{animation-name:", ";}&[data-side='right']{animation-name:", ";}&[data-side='bottom']{animation-name:", ";}&[data-side='left']{animation-name:", ";}"], get('colors.elevation.surface.default.[default]'), get('colors.border.default'), get('radii.2'), get('space.2'), get('shadows.elevation.shadow.overlay'), slideDownAndFade, slideLeftAndFade, slideUpAndFade, slideRightAndFade);
const Portal = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledMenuPortal, props, children);
};
Portal.displayName = "Portal";
const StyledMenuPortal = styled(Dropdown.Portal).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledMenuPortal",
  componentId: "sc-1uybqoa-4"
})([""]);

/**
 * The separator
 */
const Separator = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledMenuSeparator, props, children);
};
Separator.displayName = "Separator";
const StyledMenuSeparator = styled(Dropdown.Separator).withConfig({
  displayName: "RuleBuilderHeaderMenu__StyledMenuSeparator",
  componentId: "sc-1uybqoa-5"
})(["height:1px;background-color:", ";margin:", ";"], get('colors.border.default'), get('space.2'));
RuleBuilderHeaderMenu.displayName = 'RulebuilderHeaderMenu';
Item.displayName = 'RuleBuilderHeaderMenu.Item';
Trigger.displayName = 'RuleBuilderHeaderMenu.Trigger';
Portal.displayName = 'RuleBuilderHeaderMenu.Portal';
Separator.displayName = 'RuleBuilderHeaderMenu.Separator';
var RuleBuilderHeaderMenu$1 = Object.assign(RuleBuilderHeaderMenu, {
  Trigger,
  Portal,
  Item,
  Content,
  Separator
});

export { RuleBuilderHeaderMenu$1 as default };
